import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { NC_SUPPLIER_COMPLIANCE_DATA } from "../../models/data.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { NcSupplierComplianceDialogComponent } from "./nc-supplier-compliance-dialog.component";
import { NonConformiteApiService } from "../../../non-conformite/services/non-conformite/non-conformite-api.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import {
  periodBoundsFromTimeScale,
  toLocalIsoDay,
} from "../qualite-period-bounds.utils";

@Component({
  selector: "vl-widget-nc-supplier-compliance",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    ChipsComponent,
    WidgetPeriodFiltersComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-nc-supplier-compliance.component.html",
  styleUrl: "./widget-nc-supplier-compliance.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetNcSupplierComplianceComponent {
  data = input<NC_SUPPLIER_COMPLIANCE_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  private dialogsService = inject(DialogsService);
  private ncApi = inject(NonConformiteApiService);
  private widgetDataFacade = inject(WidgetDataFacadeService);

  /**
   * Échelle temporelle alignée sur `vl-widget-period-filters` :
   *   "1 m" | "6 m" | "YTD" | "EX" | "1 a" | "5 a".
   * Default = YTD. Ignoré quand `data` input est fourni (mode embedded).
   */
  timeScale = signal<string>("YTD");

  /** Données chargées localement (mode standalone). */
  localData = signal<NC_SUPPLIER_COMPLIANCE_DATA | null>(null);
  /** Indicateur de chargement local (override le metadata). */
  localLoading = signal(false);

  /** Données effectives : local si filtre appliqué, sinon data input. */
  effectiveData = computed<NC_SUPPLIER_COMPLIANCE_DATA | undefined>(
    () => this.localData() ?? this.data(),
  );

  isLoading = computed(
    () => this.localLoading() || (this.metadata()?.loading ?? false),
  );

  /** Filtre période visible uniquement en mode standalone. */
  showPeriodFilter = computed(() => !this.data());

  hasData = computed(() => {
    const d = this.effectiveData();
    return !!d && (d.worst.length > 0 || d.best.length > 0);
  });

  /** Top 5 worst sur le widget compact. */
  worstTop5 = computed(
    () => (this.effectiveData()?.worst ?? []).slice(0, 5),
  );

  bestTaux = computed(() => {
    const b = this.effectiveData()?.best?.[0];
    return b?.tauxConformite == null ? "—" : `${b.tauxConformite}%`;
  });

  /** Bornes ISO de la période active — utilisées pour fetch et passage au dialog. */
  private bounds = computed(() => {
    const { start, end } = periodBoundsFromTimeScale(this.timeScale());
    return { start: toLocalIsoDay(start), end: toLocalIsoDay(end) };
  });

  constructor() {
    effect(
      async () => {
        // Skip si data input est fourni (mode embedded — la page pilote la période).
        if (this.data()) {
          this.localData.set(null);
          return;
        }
        const { start, end } = this.bounds();
        this.localLoading.set(true);
        try {
          const d = await firstValueFrom(
            this.ncApi.supplierCompliance(start, end, undefined, 5),
          );
          this.localData.set(d);
        } finally {
          this.localLoading.set(false);
        }
      },
      { allowSignalWrites: true },
    );

    // Self-fetching : s'abonner au refreshTick global du dashboard pour
    // refetch quand l'utilisateur clique le bouton "Actualiser".
    let firstTick = true;
    effect(
      async () => {
        const tick = this.widgetDataFacade.refreshTick();
        if (firstTick) {
          firstTick = false;
          return;
        }
        void tick;
        if (this.data()) return;
        const { start, end } = this.bounds();
        this.localLoading.set(true);
        try {
          const d = await firstValueFrom(
            this.ncApi.supplierCompliance(start, end, undefined, 5),
          );
          this.localData.set(d);
        } finally {
          this.localLoading.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  /** Couleur en fonction du taux : rouge < 90, orange < 95, vert sinon. */
  tauxColor(taux: number | null): string {
    if (taux == null) return "var(--color-on-disabled)";
    if (taux < 90) return "var(--color-error)";
    if (taux < 95) return "var(--color-warning)";
    return "var(--color-valid)";
  }

  fmtTaux(v: number | null): string {
    return v == null ? "—" : `${v}%`;
  }

  openFullList() {
    if (!this.hasData()) return;
    const { start, end } = this.bounds();
    this.dialogsService.open(
      NcSupplierComplianceDialogComponent,
      "min(95vw, 1100px)",
      "fit-content",
      {
        data: this.effectiveData(),
        start,
        end,
      },
    );
  }
}
